package com.cybergstudios.rtc.engine

import com.cybergstudios.rtc.world.Paddle
import com.cybergstudios.rtc.math.Vec2
import com.cybergstudios.rtc.world.Rectangle
import kotlin.math.*

object CollisionDetection {
    /** Maintain paddles within the grid constraints */
    fun processPaddles(state: GameState) {
        processPaddle(state, state.paddlePlayer);
        processPaddle(state, state.paddleOpponent);
    }

    private fun processPaddle(state: GameState, paddle: Paddle) {
        if (paddle.x < state.canvasDimension.borderMinX) {
            paddle.leftWallHit(state)
            return;
        }

        if (paddle.x + paddle.width > state.canvasDimension.borderMaxX) {
            paddle.rightWallHit(state)
        }
    }

    fun processBall(state: GameState) {
        val ballTop = state.ball.top()
        val ballBottom = state.ball.bottom()

        // Did the ball reach the bottom?
        if (ballTop <= state.canvasDimension.borderBottom) {
            // Player lost life.
            state.ball.x = 0.0
            state.ball.y = -80.0
            state.ball.resetVel()
            state.paddleOpponent = Paddle.newPaddleOpponent()
            state.paddlePlayer = Paddle.newPaddlePlayer()
            state.decreasePlayerLife()
            return
        }

        // Did the ball reach the top?
        if (ballBottom >= state.canvasDimension.borderTop) {
            // Opponent lost life
            state.ball.x = 0.0
            state.ball.y = -80.0
            state.ball.resetVel()
            state.paddleOpponent = Paddle.newPaddleOpponent()
            state.paddlePlayer = Paddle.newPaddlePlayer()
            state.decreaseOpponentLife()
            return
        }

        val ballLeft = state.ball.left()
        val ballRight = state.ball.right()

        if (ballLeft < state.canvasDimension.borderMinX) {
            state.ball.updateX(state.canvasDimension.borderMinX + state.ball.radius)
            state.ball.invertVelX()
            return
        }

        if (ballRight > state.canvasDimension.borderMaxX) {
            state.ball.updateX(state.canvasDimension.borderMaxX - state.ball.radius)
            state.ball.invertVelX()
        }
    }

    fun processBallAndPaddles(state: GameState) {
        processBallAndPaddle(state.paddlePlayer, state)
        processBallAndPaddle(state.paddleOpponent, state)
    }

    private fun processBallAndPaddle(paddle: Paddle, state: GameState) {
        if (!isBallAndDiscCollision(paddle, state)) { return }

        // http://gamedev.stackexchange.com/questions/4253/in-pong-how-do-you-calculate-the-balls-direction-when-it-bounces-off-the-paddl
        // [ - width / 2 .. + width / 2 ]
        val paddleHalfWidth = paddle.width / 2
        val relativeToPaddle = paddle.x + paddleHalfWidth - state.ball.x
        val relativeToPaddleBounded = min(max(relativeToPaddle, -1 * paddleHalfWidth), paddleHalfWidth)
        val normalizedRelativeToPaddle = relativeToPaddleBounded / paddleHalfWidth
        val maxDistortionAngle = PI / 4
        val angle = normalizedRelativeToPaddle * maxDistortionAngle

        val velocityMagnitude = state.ball.vel.magnitude()
        val velX = velocityMagnitude * sin(angle)
        val velY = velocityMagnitude * cos(angle)

        val isBallInTheHalfBottom = state.ball.y <= 0

        // Update ball velocity
        state.ball.vel = Vec2(velX, if (isBallInTheHalfBottom) velY else -1 * velY)

        // Push the ball out of the paddle
        if (isBallInTheHalfBottom) {
            state.ball.y = paddle.y + state.ball.radius;
        } else {
            state.ball.y = paddle.y - paddle.height - state.ball.radius;
        }
    }

    private fun isBallAndDiscCollision(paddle: Paddle, state: GameState): Boolean {
        // let's first check the y coordinate to see if there's a chance of the ball
        // be touching the paddle.
        val ballTop = state.ball.top()
        val ballBottom = state.ball.bottom()
        val paddleTop = paddle.y
        val paddleBottom = paddle.y - paddle.height

        val yCandidate = (ballTop in paddleBottom..paddleTop) ||
                (ballBottom in paddleBottom..paddleTop)

        if (!yCandidate) return false;

        // let's now check if the ball is touching the paddle
        val ballRight = state.ball.right()
        val ballLeft = state.ball.left()
        val paddleRight = paddle.x + paddle.width
        val paddleLeft = paddle.x

        return (ballLeft in paddleLeft..paddleRight) ||
                (ballRight in paddleLeft..paddleRight)
    }

    fun processBallAndBlocks(state: GameState) {
        state.grid.polygons.forEach lit@{ polygon ->
            if (!polygon.enabled) { return@lit }
            // TODO: check mainRectangle to avoid checking each point at a polygon

            polygon.rectangles.forEach { rect ->
                if (hitRectangle(state, rect)) {
                    // tell the polygon that it was hit by the ball
                    // TODO: we need to identify if the actor is the player or the opponent
                    val points = polygon.hit()
                    // if we've cleared all the blocks the player has won
                    state.polygonHit(points)
                    // invert the ball y direction
                    state.ball.vel = Vec2(state.ball.vel.x, state.ball.vel.y * -1)
                    // early exit
                    return
                }
            }
        }
    }

    private fun hitRectangle(state: GameState, rect: Rectangle): Boolean {
        val ballLeft = state.ball.left()
        val ballRight = state.ball.right()
        val ballTop = state.ball.top()
        val ballBottom = state.ball.bottom()

        val blockTop = rect.topLeft.y
        val blockBottom = rect.bottomRight.y
        val blockLeft = rect.topLeft.x
        val blockRight = rect.bottomRight.x

        // check if the ball is in the y range of the block
        val yInRange = (ballTop in blockBottom..blockTop) ||
                (ballBottom in blockBottom..blockTop)
        if (!yInRange) return false

        val xInRange = (ballRight in blockLeft..blockRight) ||
                (ballLeft <= blockRight && ballRight >= blockLeft)

        if (!xInRange) return false

        return true
    }

}
